/**
 * Iter 145t — Mobile-only fixed bottom navigation.
 * Iter 204c — Added "Markets" tab that opens a slide-up sheet listing
 *             Halftime · Double Chance · Asian Handicap · Goal Timing.
 *
 * Six tap-target tabs spanning the most-used routes: Home, Schedule,
 * Live, Markets (sheet), History, Account.  Renders ONLY at <md
 * (Tailwind breakpoint 768px) — desktop / iPad-landscape continue to
 * use the top header's Markets dropdown.
 *
 * Tap targets are 44px min-height per Apple HIG; the bar itself sits
 * above the iOS home-indicator via env(safe-area-inset-bottom) and
 * uses backdrop-blur for a native iOS / Android M3 feel.
 */
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home, CalendarDays, Activity, History, User,
  LayoutGrid, Clock, ShieldCheck, Target, X, Sparkles,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const TABS = [
  { kind: "link",   to: "/",         label: "Home",     icon: Home,         match: (p) => p === "/" || p === "" },
  { kind: "link",   to: "/schedule", label: "Schedule", icon: CalendarDays, match: (p) => p.startsWith("/schedule") },
  { kind: "link",   to: "/live",     label: "Live",     icon: Activity,     match: (p) => p.startsWith("/live") },
  { kind: "sheet",  to: "#markets",  label: "Markets",  icon: LayoutGrid,
    match: (p) => ["/halftime", "/double-chance", "/asian-handicap", "/goal-timing"].some((r) => p.startsWith(r)) },
  { kind: "link",   to: "/history",  label: "History",  icon: History,      match: (p) => p.startsWith("/history") || p.startsWith("/accuracy") },
  { kind: "link",   to: "/account",  label: "Account",  icon: User,         match: (p) => p.startsWith("/account") || p.startsWith("/profile") },
];

const MARKETS_SHEET_LINKS = [
  { to: "/halftime",       label: "Halftime",       icon: Clock,
    blurb: "Pre-match HT predictions & best halftime markets." },
  { to: "/double-chance",  label: "Double Chance",  icon: ShieldCheck,
    blurb: "Highest hit-rate market on the platform." },
  { to: "/asian-handicap", label: "Asian Handicap", icon: Target,
    blurb: "xGD, Monte Carlo, line-by-line edge & ROI." },
  { to: "/goal-timing",    label: "Goal Timing",    icon: LayoutGrid,
    blurb: "Quarter probabilities, HT/SH markets, late-goal hazard." },
];


export default function MobileBottomNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [marketsOpen, setMarketsOpen] = useState(false);

  // Hide on the public-facing landing/auth/share routes — these don't
  // benefit from app-chrome and look better full-bleed.
  const HIDE_ON = ["/auth", "/share"];
  if (HIDE_ON.some((p) => pathname.startsWith(p))) return null;

  const handleSheetLink = (to) => {
    setMarketsOpen(false);
    navigate(to);
  };

  return (
    <>
      {/* Iter 251 — GOLD Membership floating chip (mobile).
          Visible above the bottom-nav when user is GOLD or admin (admins are implicitly GOLD).
          Direct link to /gold — no need to open Markets sheet. */}
      {(user?.is_gold || user?.is_admin) && (
        <Link
          to="/gold"
          data-testid="gold-mobile-chip"
          aria-label="GOLD Membership"
          className="md:hidden fixed z-[55] right-3 bg-gradient-to-r from-amber-500 to-yellow-400 text-black font-bold text-[11px] tracking-widest uppercase shadow-lg shadow-amber-500/40 rounded-full px-3.5 py-1.5 flex items-center gap-1.5"
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 72px)" }}
        >
          <Sparkles className="w-3.5 h-3.5" />
          <span>GOLD</span>
        </Link>
      )}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-50 h-bottomnav pb-safe backdrop-blur-xl"
        data-testid="mobile-bottom-nav"
        role="navigation"
        aria-label="Primary"
        style={{
          background: "rgba(9,9,11,0.9)",
          borderTop: "1px solid var(--v3-line)",
        }}
      >
        <ul className="grid grid-cols-6 h-16">
          {TABS.map(({ kind, to, label, icon: Icon, match }) => {
            const active = match(pathname);
            const testId = `bottom-nav-${label.toLowerCase()}`;
            const commonCx = `flex-1 flex flex-col items-center justify-center min-touch gap-1 transition-colors relative ${
              active ? "text-foreground" : "text-muted-foreground/70 hover:text-foreground"
            }`;
            if (kind === "sheet") {
              return (
                <li key={label} className="flex">
                  <button
                    type="button"
                    onClick={() => setMarketsOpen(true)}
                    data-testid={testId}
                    aria-haspopup="dialog"
                    aria-expanded={marketsOpen}
                    className={commonCx}
                  >
                    <Icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
                    <span
                      className="text-[10px] leading-none"
                      style={{
                        letterSpacing: "-0.005em",
                        fontWeight: active ? 500 : 400,
                      }}
                    >
                      {label}
                    </span>
                    {active && (
                      <span
                        className="absolute top-0 h-px w-10 rounded-full"
                        style={{ background: "var(--v3-text)" }}
                        aria-hidden
                      />
                    )}
                  </button>
                </li>
              );
            }
            return (
              <li key={to} className="flex">
                <Link
                  to={to}
                  data-testid={testId}
                  className={commonCx}
                  aria-current={active ? "page" : undefined}
                >
                  <Icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
                  <span
                    className="text-[10px] leading-none"
                    style={{
                      letterSpacing: "-0.005em",
                      fontWeight: active ? 500 : 400,
                    }}
                  >
                    {label}
                  </span>
                  {active && (
                    <span
                      className="absolute top-0 h-px w-10 rounded-full"
                      style={{ background: "var(--v3-text)" }}
                      aria-hidden
                    />
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {marketsOpen && (
        <MarketsSheet
          onClose={() => setMarketsOpen(false)}
          onNavigate={handleSheetLink}
          pathname={pathname}
        />
      )}
    </>
  );
}


/**
 * Slide-up bottom sheet listing the four market sub-pages.  Closes on
 * backdrop tap or close-button tap.  Each item shows an icon, label,
 * and a one-line blurb to help users pick the right page.
 */
function MarketsSheet({ onClose, onNavigate, pathname }) {
  return (
    <div
      className="md:hidden fixed inset-0 z-[60] flex items-end"
      role="dialog"
      aria-modal="true"
      aria-label="Markets"
      data-testid="markets-sheet"
    >
      {/* Backdrop */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close markets sheet"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        data-testid="markets-sheet-backdrop"
      />
      {/* Sheet body */}
      <div className="relative w-full bg-background border-t border-border rounded-t-2xl shadow-2xl pb-safe"
           style={{ animation: "marketsSheetIn 180ms cubic-bezier(0.16, 1, 0.3, 1)" }}>
        <style>{`@keyframes marketsSheetIn { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <h2 className="text-base font-semibold tracking-tight">Markets</h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="markets-sheet-close"
            className="p-1 -mr-1 text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="px-5 text-xs text-muted-foreground mb-3">
          Per-market analysis pages — each comes with its own engine,
          performance tracker, and calibration board.
        </p>
        <ul className="divide-y divide-border/60">
          {MARKETS_SHEET_LINKS.map(({ to, label, icon: Icon, blurb }) => {
            const active = pathname.startsWith(to);
            return (
              <li key={to}>
                <button
                  type="button"
                  onClick={() => onNavigate(to)}
                  data-testid={`markets-sheet-item-${to.replace(/\//g, "")}`}
                  className={`w-full flex items-start gap-3 px-5 py-3.5 text-left transition-colors ${
                    active ? "bg-foreground/[0.04]" : "hover:bg-foreground/[0.04]"
                  }`}
                >
                  <span className={`mt-0.5 inline-flex w-9 h-9 items-center justify-center rounded-lg ${
                    active ? "bg-foreground text-background" : "bg-muted text-foreground"
                  }`}>
                    <Icon className="w-4 h-4" />
                  </span>
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-foreground">{label}</span>
                    <span className="block text-xs text-muted-foreground leading-snug">{blurb}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
